import { css } from "@emotion/react";
import { useState } from "react";

import { Animation, Colors, Link } from "@zapier/design-system";

import {
  createKeyHandler,
  getResponsiveStyles,
} from "@zapier/reporting/src/utils";

// Copy/pasted from `design-system` because flowgen bug
export type AnchorTarget = "_self" | "_blank" | "_parent" | "_top";

type ListItemProps = {
  className?: string;
  /** Content of list item */
  children: React.ReactNode;
  /** used to render as a Link */
  href?: string;
  /** true to supress focus / hover styles */
  isReadOnly?: boolean;
  /** true to style as selected */
  isSelected?: boolean;
  /** optional click handler */
  onClick?: () => void;
  /** optional select handler */
  onSelect?: () => void;
  /** Anchor target if is link */
  target?: AnchorTarget;
};

const clickableStyle = css`
  cursor: pointer;
`;

const focusedStyle = css`
  background-color: ${Colors.UiPrimaryWeakest};
`;

const linkStyle = css`
  display: block;
  text-decoration: none;
  width: 100%;
`;

const selectedStyled = css`
  background-color: ${Colors.UiPrimaryWeakest};
`;

const rootStyle = getResponsiveStyles({
  mobile: css`
    border-radius: var(--zds-radius-8);
    border: var(--zds-border-weak);
    padding: var(--zds-space-16);
    transition: ${Animation.transitionValue};
  `,
  tablet: css`
    padding: var(--zds-space-16) var(--zds-space-24);
  `,
});

function ListItem({
  className,
  children,
  href,
  isReadOnly = false,
  isSelected,
  onClick,
  onSelect,
  target,
}: ListItemProps) {
  const [isFocused, setIsFocused] = useState(false);

  const focusHandlers = isReadOnly
    ? {}
    : {
        onBlur: () => setIsFocused(false),
        onFocus: () => setIsFocused(true),
        onMouseEnter: () => setIsFocused(true),
        onMouseLeave: () => setIsFocused(false),
      };

  const linkHoverHandlers = isReadOnly
    ? {}
    : {
        onHoverStart: () => setIsFocused(true),
        onHoverEnd: () => setIsFocused(false),
      };

  const clickHandlers =
    !isReadOnly && (onClick || onSelect)
      ? {
          onClick: onClick,
          onKeyDown: createKeyHandler(["Enter", " "], (e) => {
            if (e.key === " " && onSelect) {
              onSelect();
            } else if (onClick) {
              onClick();
            }
          }),
          role: "button",
          tabIndex: 0,
        }
      : {};

  const item = (
    <div
      className={className}
      css={[
        rootStyle,
        isFocused && focusedStyle,
        isSelected && selectedStyled,
        (onClick || href) && clickableStyle,
      ]}
      {...(href ? {} : clickHandlers)}
      {...(href ? {} : focusHandlers)}
    >
      {children}
    </div>
  );

  return href ? (
    <Link
      css={linkStyle}
      href={href}
      target={target}
      onBlur={focusHandlers.onBlur}
      onFocus={focusHandlers.onFocus}
      {...linkHoverHandlers}
      {...(onClick ? { onPress: onClick } : {})}
    >
      {item}
    </Link>
  ) : (
    item
  );
}

export default ListItem;
